import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Search, 
  UserCheck,
  Mail,
  Phone,
  Activity,
  DollarSign,
  Trash2,
  Pencil,
  Lock
} from 'lucide-react';
import { crewAPI } from '../services/api';
import AddCrewDialog from '../components/AddCrewDialog';
import EditCrewDialog from '../components/EditCrewDialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const Crew = () => {
  const { canCreate, canEdit, canDelete } = useAuth();
  const [crewData, setCrewData] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchCrew();
  }, [selectedStatus]);

  const fetchCrew = async () => {
    try {
      setLoading(true);
      const [crewRes, statsRes] = await Promise.all([
        crewAPI.getAll({ status: selectedStatus }),
        crewAPI.getStats()
      ]);
      setCrewData(crewRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Fout bij ophalen personeel:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (crewId, crewName) => {
    try {
      await crewAPI.delete(crewId);
      toast({
        title: "Gelukt",
        description: `Personeelslid "${crewName}" succesvol verwijderd`
      });
      fetchCrew();
    } catch (error) {
      console.error('Fout bij verwijderen personeelslid:', error);
      toast({
        title: "Fout",
        description: "Kan personeelslid niet verwijderen",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (crew) => {
    setSelectedCrew(crew);
    setEditDialogOpen(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      'all': 'Alle Statussen',
      'available': 'Beschikbaar',
      'booked': 'Ingepland'
    };
    return labels[status] || status;
  };

  const statuses = ['all', 'available', 'booked'];

  const filteredCrew = crewData.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.role.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const availableCrew = stats?.availableCrew || 0;
  const avgRate = stats?.avgRate || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Personeel laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Personeel Beheer</h1>
          <p className="text-gray-500 mt-1">Beheer personeelsleden en hun planning</p>
        </div>
        {canCreate() ? (
          <AddCrewDialog onSuccess={fetchCrew} />
        ) : (
          <div className="flex items-center text-gray-500 text-sm">
            <Lock className="w-4 h-4 mr-1" />
            Alleen lezen
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totaal Personeel</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats?.totalCrew || 0}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-lg">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Nu Beschikbaar</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{availableCrew}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                <Activity className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Momenteel Ingepland</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats?.bookedCrew || 0}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Gem. Tarief/Dag</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">€{avgRate}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-yellow-50 to-red-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-lg">
              <UserCheck className="w-5 h-5 mr-2 text-yellow-600" />
              Personeel Overzicht
            </CardTitle>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Zoek personeel..."
                  className="pl-10 w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-crew-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex border-b bg-gray-50 p-4 space-x-2 overflow-x-auto">
            {statuses.map(status => (
              <Button
                key={status}
                variant={selectedStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedStatus(status)}
                className={selectedStatus === status ? 'bg-gradient-to-r from-yellow-600 to-red-600' : ''}
              >
                {getStatusLabel(status)}
              </Button>
            ))}
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Naam</TableHead>
                <TableHead className="font-semibold">Functie</TableHead>
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Vaardigheden</TableHead>
                <TableHead className="font-semibold">Beschikbaarheid</TableHead>
                <TableHead className="font-semibold">Dag Tarief</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCrew.map((member) => (
                <TableRow key={member.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <TableCell className="font-medium">{member.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                      {member.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-3 h-3 mr-1" />
                        {member.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-3 h-3 mr-1" />
                        {member.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {member.skills?.slice(0, 2).map((skill, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          {skill}
                        </Badge>
                      ))}
                      {member.skills?.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{member.skills.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{member.availability}%</span>
                      </div>
                      <Progress value={member.availability} className="h-2" />
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">€{member.ratePerDay}</TableCell>
                  <TableCell>
                    <Badge className={`${
                      member.status === 'available' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                      'bg-blue-100 text-blue-700 hover:bg-blue-100'
                    }`}>
                      {member.status === 'available' ? 'Beschikbaar' : 'Ingepland'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {canEdit() && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-8"
                          onClick={() => handleEdit(member)}
                          data-testid={`edit-crew-${member.id}-btn`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {canDelete() && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-crew-${member.id}-btn`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Personeelslid Verwijderen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je "{member.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(member.id, member.name)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="confirm-delete-crew-btn"
                              >
                                Verwijderen
                              </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EditCrewDialog
        crew={selectedCrew}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={fetchCrew}
      />
    </div>
  );
};

export default Crew;
